import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Stack, Alert } from '@mui/material';
import { createPurchase, getStock } from '../../services/stocksApi';
import { CreatePurchaseCommand, StockDto, InvestmentType } from '../../types/stocks';

interface AddPurchaseDialogProps {
  open: boolean;
  stockPublicId: string | null;
  onClose: () => void;
  onCreated: () => void;
}

const AddPurchaseDialog: React.FC<AddPurchaseDialogProps> = ({ open, stockPublicId, onClose, onCreated }) => {
  const [date, setDate] = useState<string>(() => new Date().toISOString().substring(0,10));
  const [quantity, setQuantity] = useState<string>('');
  const [pricePerShare, setPricePerShare] = useState<string>('');
  const [cambio, setCambio] = useState<string>('');
  const [stock, setStock] = useState<StockDto | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setDate(new Date().toISOString().substring(0,10));
    setQuantity('');
    setPricePerShare('');
    setCambio('');
    
    setError(null);
  };

  const handleClose = () => {
    if (!submitting) {
      reset();
      onClose();
    }
  };

  const handleSubmit = async () => {
    if (!stockPublicId) return;
    setError(null);
    if (!quantity || !pricePerShare) {
      setError('A quantidade e o preço são obrigatórios');
      return;
    }
    const q = parseFloat(quantity);
    const p = parseFloat(pricePerShare);
    if (q <= 0 || p <= 0) {
      setError('Os valores têm de ser positivos');
      return;
    }
    const hasCambio = cambio !== '';
    const cambioNum = hasCambio ? Number(cambio) : NaN;
    if (hasCambio && (isNaN(cambioNum) || cambioNum <= 0)) {
      setError('O câmbio tem de ser um número positivo');
      return;
    }
    setSubmitting(true);
    try {
      const command: CreatePurchaseCommand = {
        date: date,
        quantity: q,
        pricePerShare: p,
        cambio: hasCambio ? cambioNum : undefined
      };
      await createPurchase(stockPublicId, command);
      onCreated();
      reset();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Não foi possível adicionar a compra');
    } finally {
      setSubmitting(false);
    }
  };

  // Load the stock when the dialog opens (its type sets the input precision)
  useEffect(() => {
    const load = async () => {
      if (!open || !stockPublicId) return;
      try {
        setStock(await getStock(stockPublicId));
      } catch {
        // Ignore load errors here; user can still input and submit
      }
    };
    load();
  }, [open, stockPublicId]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Adicionar compra</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Data"
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
          />
          <TextField
            label="Quantidade"
            type="number"
            value={quantity}
            onChange={e => setQuantity(e.target.value)}
            inputProps={{ step: stock?.investmentType === InvestmentType.Cryptocurrency ? '0.000000001' : '0.01', min: '0' }}
            required
          />
          <TextField
            label="Preço por unidade"
            type="number"
            value={pricePerShare}
            onChange={e => setPricePerShare(e.target.value)}
            inputProps={{ step: stock?.investmentType === InvestmentType.Cryptocurrency ? '0.000000001' : '0.01', min: '0' }}
            required
          />
          <TextField
            label="Câmbio (moeda do título → EUR)"
            type="number"
            value={cambio}
            onChange={e => setCambio(e.target.value)}
            inputProps={{ step: stock?.investmentType === InvestmentType.Cryptocurrency ? '0.000000001' : '0.000001', min: '0' }}
          />
          
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>Cancelar</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={submitting}>Adicionar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPurchaseDialog;